import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowUpDown,
  Building2,
  CheckCircle2,
  GraduationCap,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  UserRound,
  Wrench,
} from 'lucide-react';
import { apiGet, apiPost, ApiError } from '../../lib/api';

type ProviderTypeFilter = 'ALL' | 'STRINGER' | 'COACH' | 'ACADEMY';
type PublicationStatusFilter =
  | 'SUBMITTED'
  | 'VERIFIED'
  | 'PUBLISHED'
  | 'SUSPENDED'
  | 'ARCHIVED'
  | 'ALL';
type ProviderReviewAction = 'VERIFY' | 'REJECT' | 'PUBLISH' | 'SUSPEND' | 'ARCHIVE';
type QueueSortBy =
  | 'UPDATED_AT'
  | 'CREATED_AT'
  | 'LAST_REVIEWED_AT'
  | 'REPORTED_COUNT'
  | 'DISPLAY_NAME'
  | 'PUBLISHED_AT'
  | 'VERIFIED_AT';
type QueueSortDir = 'DESC' | 'ASC';

interface ProviderReviewQueueItem {
  providerId: number;
  userId: number;
  displayName: string;
  providerTypes: string[];
  publicationStatus: string;
  verificationLevel?: string | null;
  active: boolean;
  publiclyVisible: boolean;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  tenantId?: number | null;
  reportedCount?: number | null;
  pendingReportCount: number;
  topPendingReasonCode?: string | null;
  readinessStatus: string;
  blockingIssueCount: number;
  warningIssueCount: number;
  lastReportedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastReviewedAt?: string | null;
}

interface ProviderReportAdminDto {
  id: number;
  reasonCode?: string | null;
  reasonText?: string | null;
  status: string;
  reportedByUserId: number;
  createdAt: string;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
}

interface ProviderReportReasonSummaryDto {
  reasonCode: string;
  totalCount: number;
  pendingCount: number;
  lastReportedAt?: string | null;
}

interface ProviderModerationSummaryDto {
  totalReports: number;
  pendingReports: number;
  reviewedReports: number;
  dismissedReports: number;
  topPendingReasonCode?: string | null;
  lastReportedAt?: string | null;
  reasonBreakdown: ProviderReportReasonSummaryDto[];
}

interface ProviderReviewDetail extends ProviderReviewQueueItem {
  bio?: string | null;
  sports?: string[] | null;
  address?: string | null;
  serviceRadiusMiles?: number | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  contactWhatsapp?: string | null;
  websiteUrl?: string | null;
  preferredContactMethod?: string | null;
  instagramHandle?: string | null;
  facebookUrl?: string | null;
  linkedinUrl?: string | null;
  publishedAt?: string | null;
  verifiedAt?: string | null;
  readyToPublish: boolean;
  blockingIssues: string[];
  warningIssues: string[];
  stringerDetails?: {
    experienceYears?: number | null;
    stringBrands?: string[] | null;
    basePriceMin?: number | null;
    basePriceMax?: number | null;
    turnaroundHours?: number | null;
  } | null;
  coachDetails?: {
    experienceYears?: number | null;
    lessonTypes?: string[] | null;
    specialties?: string[] | null;
    hourlyRateMin?: number | null;
    hourlyRateMax?: number | null;
    availabilityNotes?: string | null;
  } | null;
  academyDetails?: {
    academyName?: string | null;
    numCourts?: number | null;
    programsOffered?: string[] | null;
    amenities?: string[] | null;
  } | null;
  moderationSummary: ProviderModerationSummaryDto;
  reports: ProviderReportAdminDto[];
}

interface ProviderReviewStats {
  submittedCount: number;
  verifiedCount: number;
  publishedCount: number;
  suspendedCount: number;
  archivedCount: number;
  pendingReportCount: number;
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const typeOptions: Array<{ key: ProviderTypeFilter; label: string; icon: typeof Wrench }> = [
  { key: 'ALL', label: 'All', icon: GraduationCap },
  { key: 'STRINGER', label: 'Stringers', icon: Wrench },
  { key: 'COACH', label: 'Coaches', icon: UserRound },
  { key: 'ACADEMY', label: 'Academies', icon: Building2 },
];

const statusOptions: PublicationStatusFilter[] = ['SUBMITTED', 'VERIFIED', 'PUBLISHED', 'SUSPENDED', 'ARCHIVED', 'ALL'];

const sortOptions: Array<{ value: QueueSortBy; label: string }> = [
  { value: 'UPDATED_AT', label: 'Recently updated' },
  { value: 'CREATED_AT', label: 'Newest submitted' },
  { value: 'LAST_REVIEWED_AT', label: 'Last reviewed' },
  { value: 'REPORTED_COUNT', label: 'Most reports' },
  { value: 'DISPLAY_NAME', label: 'Name' },
  { value: 'PUBLISHED_AT', label: 'Published date' },
  { value: 'VERIFIED_AT', label: 'Verified date' },
];

function formatWhen(value?: string | null): string {
  if (!value) return 'Not yet';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusTone(status: string) {
  switch (status) {
    case 'SUBMITTED':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'VERIFIED':
      return 'bg-sky-500/15 text-sky-300 border-sky-500/30';
    case 'PUBLISHED':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'SUSPENDED':
      return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
    case 'ARCHIVED':
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
    default:
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
  }
}

function getReadinessTone(status: string) {
  switch (status) {
    case 'READY':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'BLOCKED':
      return 'bg-rose-500/15 text-rose-300 border-rose-500/30';
    case 'NEEDS_REVIEW':
    default:
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  }
}

function getAllowedActions(detail: ProviderReviewDetail | null): ProviderReviewAction[] {
  if (!detail) return [];
  switch (detail.publicationStatus) {
    case 'SUBMITTED':
      return ['VERIFY', 'REJECT'];
    case 'VERIFIED':
      return ['PUBLISH', 'ARCHIVE'];
    case 'PUBLISHED':
      return ['SUSPEND', 'ARCHIVE'];
    case 'SUSPENDED':
      return ['PUBLISH', 'ARCHIVE'];
    default:
      return [];
  }
}

function actionLabel(action: ProviderReviewAction) {
  switch (action) {
    case 'VERIFY':
      return 'Approve Review';
    case 'REJECT':
      return 'Reject';
    case 'PUBLISH':
      return 'Publish';
    case 'SUSPEND':
      return 'Suspend';
    case 'ARCHIVE':
      return 'Archive';
    default:
      return action;
  }
}

export function ProviderReviewPage({ initialType = 'ALL' }: { initialType?: ProviderTypeFilter }) {
  const [items, setItems] = useState<ProviderReviewQueueItem[]>([]);
  const [stats, setStats] = useState<ProviderReviewStats | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ProviderReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ProviderTypeFilter>(initialType);
  const [statusFilter, setStatusFilter] = useState<PublicationStatusFilter>('SUBMITTED');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<QueueSortBy>('UPDATED_AT');
  const [sortDir, setSortDir] = useState<QueueSortDir>('DESC');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actioning, setActioning] = useState<ProviderReviewAction | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        providerType: typeFilter,
        status: statusFilter,
        sortBy,
        sortDir,
        page: String(page),
        size: '20',
      });
      if (searchQuery.trim()) {
        params.set('search', searchQuery.trim());
      }

      const response = await apiGet<PageResponse<ProviderReviewQueueItem>>(
        `/providers/review?${params.toString()}`
      );
      setItems(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalElements);
      if (response.content.length > 0 && !response.content.some((item) => item.providerId === selectedId)) {
        setSelectedId(response.content[0].providerId);
      }
      if (response.content.length === 0) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (loadError) {
      console.error('Failed to load provider review queue', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load provider review queue');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, selectedId, sortBy, sortDir, statusFilter, typeFilter]);

  const loadStats = useCallback(async () => {
    try {
      const response = await apiGet<ProviderReviewStats>('/providers/review/stats');
      setStats(response);
    } catch (loadError) {
      console.error('Failed to load provider review stats', loadError);
    }
  }, []);

  const loadDetail = useCallback(async (providerId: number) => {
    setDetailLoading(true);
    try {
      const response = await apiGet<ProviderReviewDetail>(`/providers/review/${providerId}`);
      setDetail(response);
      setReviewNotes('');
    } catch (loadError) {
      console.error('Failed to load provider review detail', loadError);
      setError(loadError instanceof Error ? loadError.message : 'Failed to load provider detail');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadQueue();
    void loadStats();
  }, [loadQueue, loadStats]);

  useEffect(() => {
    if (selectedId != null) {
      void loadDetail(selectedId);
    }
  }, [loadDetail, selectedId]);

  const handleAction = async (action: ProviderReviewAction) => {
    if (!detail) return;
    setActioning(action);
    setError(null);
    try {
      const response = await apiPost<ProviderReviewDetail>(`/providers/review/${detail.providerId}/action`, {
        action,
        reviewNotes: reviewNotes.trim() || undefined,
      });
      setDetail(response);
      await loadQueue();
      await loadStats();
    } catch (actionError) {
      console.error('Failed to apply provider review action', actionError);
      if (actionError instanceof ApiError) {
        setError(actionError.message);
      } else {
        setError(actionError instanceof Error ? actionError.message : 'Failed to apply review action');
      }
    } finally {
      setActioning(null);
    }
  };

  const queueSummary = useMemo(() => {
    return [
      {
        label: 'Submitted',
        value: stats?.submittedCount ?? 0,
        tone: 'text-amber-300 border-amber-500/20 bg-amber-500/10',
      },
      {
        label: 'Verified',
        value: stats?.verifiedCount ?? 0,
        tone: 'text-sky-300 border-sky-500/20 bg-sky-500/10',
      },
      {
        label: 'Published',
        value: stats?.publishedCount ?? 0,
        tone: 'text-emerald-300 border-emerald-500/20 bg-emerald-500/10',
      },
      {
        label: 'Pending Reports',
        value: stats?.pendingReportCount ?? 0,
        tone: 'text-rose-300 border-rose-500/20 bg-rose-500/10',
      },
    ];
  }, [stats]);

  const publishBlocked = detail != null && detail.blockingIssues.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold text-white">
            <Shield className="h-7 w-7 text-cyan-400" />
            PROs Review Workflow
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Review submitted public listings, publish approved providers, and manage marketplace visibility.
          </p>
        </div>

        <button
          onClick={() => {
            void loadQueue();
            void loadStats();
          }}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-slate-300 transition-colors hover:bg-slate-700 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      <div className="mb-4 grid grid-cols-4 gap-4">
        {queueSummary.map((item) => (
          <div key={item.label} className={`rounded-lg border p-4 ${item.tone}`}>
            <div className="text-2xl font-bold">{item.value}</div>
            <div className="text-sm text-slate-300">{item.label}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1 rounded-lg bg-slate-800 p-1">
          {typeOptions.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => {
                setTypeFilter(key);
                setPage(0);
              }}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                typeFilter === key
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 rounded-lg bg-slate-800 p-1">
          {statusOptions.map((status) => (
            <button
              key={status}
              onClick={() => {
                setStatusFilter(status);
                setPage(0);
              }}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                statusFilter === status
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:bg-slate-700 hover:text-white'
              }`}
            >
              {status === 'ALL' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        <div className="relative min-w-[16rem] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setPage(0);
            }}
            placeholder="Search name, city, zip, email, phone"
            className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
          <ArrowUpDown className="h-4 w-4 text-slate-500" />
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value as QueueSortBy)}
            className="bg-transparent text-sm text-slate-300 focus:outline-none"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value} className="bg-slate-950">
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setSortDir((current) => (current === 'DESC' ? 'ASC' : 'DESC'))}
            className="rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 transition-colors hover:bg-slate-700"
          >
            {sortDir === 'DESC' ? 'Desc' : 'Asc'}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        <div className="flex-1 overflow-y-auto">
          {error ? (
            <div className="mb-4 flex items-center gap-3 rounded-lg border border-red-800/50 bg-red-900/20 p-4 text-red-400">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Shield className="mb-3 h-12 w-12 opacity-50" />
              <p>No provider listings found</p>
              <p className="mt-1 text-sm">Try another publication status, search term, or provider type.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((item) => (
                <button
                  key={item.providerId}
                  onClick={() => setSelectedId(item.providerId)}
                  className={`rounded-xl border p-4 text-left transition-colors ${
                    selectedId === item.providerId
                      ? 'border-cyan-500/60 bg-cyan-500/10'
                      : 'border-slate-800 bg-slate-900/70 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-white">{item.displayName}</p>
                      <p className="mt-1 text-sm text-slate-400">
                        {item.providerTypes.join(', ')} •{' '}
                        {[item.city, item.state].filter(Boolean).join(', ') || item.zipcode || 'Location on file'}
                      </p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusTone(item.publicationStatus)}`}>
                      {item.publicationStatus}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
                    <span>{item.publiclyVisible ? 'Public' : 'Private'}</span>
                    <span>•</span>
                    <span>{item.active ? 'Active' : 'Inactive'}</span>
                    <span>•</span>
                    <span>{item.pendingReportCount} pending reports</span>
                    {item.topPendingReasonCode ? (
                      <>
                        <span>•</span>
                        <span>Top issue {item.topPendingReasonCode}</span>
                      </>
                    ) : null}
                    {item.lastReportedAt ? (
                      <>
                        <span>•</span>
                        <span>Last report {formatWhen(item.lastReportedAt)}</span>
                      </>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${getReadinessTone(item.readinessStatus)}`}>
                      {item.readinessStatus === 'NEEDS_REVIEW' ? 'Needs review' : item.readinessStatus}
                    </span>
                    {item.blockingIssueCount > 0 ? (
                      <span className="rounded-full border border-rose-500/20 bg-rose-500/10 px-2.5 py-1 text-[11px] text-rose-300">
                        {item.blockingIssueCount} blocking
                      </span>
                    ) : null}
                    {item.warningIssueCount > 0 ? (
                      <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-300">
                        {item.warningIssueCount} warning
                      </span>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}

          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4">
              <div className="text-sm text-slate-500">
                Showing {items.length} of {totalItems} listings
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  disabled={page === 0}
                  className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-700 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-slate-400">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                  disabled={page >= totalPages - 1}
                  className="rounded-lg bg-slate-800 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="w-[28rem] flex-shrink-0 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/70 p-4">
          {detailLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : detail ? (
            <div className="space-y-5">
              <div className="border-b border-slate-800 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-white">{detail.displayName}</h2>
                    <p className="mt-1 text-sm text-slate-400">{detail.providerTypes.join(', ')}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusTone(detail.publicationStatus)}`}>
                    {detail.publicationStatus}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
                  <span>User #{detail.userId}</span>
                  <span>•</span>
                  <span>{detail.publiclyVisible ? 'Public' : 'Private'}</span>
                  <span>•</span>
                  <span>{detail.active ? 'Active' : 'Inactive'}</span>
                  <span>•</span>
                  <span>{detail.pendingReportCount} pending reports</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getReadinessTone(detail.readinessStatus)}`}>
                    {detail.readinessStatus === 'NEEDS_REVIEW' ? 'Needs review' : detail.readinessStatus}
                  </span>
                  {detail.readyToPublish ? (
                    <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs text-emerald-300">
                      Ready to publish
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <StatChip label="Created" value={formatWhen(detail.createdAt)} />
                <StatChip label="Updated" value={formatWhen(detail.updatedAt)} />
                <StatChip label="Verified" value={formatWhen(detail.verifiedAt)} />
                <StatChip label="Published" value={formatWhen(detail.publishedAt)} />
              </div>

              <Section title="Publish readiness">
                {detail.blockingIssues.length === 0 && detail.warningIssues.length === 0 ? (
                  <p className="text-sm text-emerald-300">
                    No blocking issues found. This listing is structurally ready for publication.
                  </p>
                ) : null}
                {detail.blockingIssues.length > 0 ? (
                  <IssueList
                    tone="rose"
                    title="Blocking issues"
                    items={detail.blockingIssues}
                  />
                ) : null}
                {detail.warningIssues.length > 0 ? (
                  <div className={detail.blockingIssues.length > 0 ? 'mt-3' : ''}>
                    <IssueList
                      tone="amber"
                      title="Warnings to review"
                      items={detail.warningIssues}
                    />
                  </div>
                ) : null}
              </Section>

              <Section title="Public listing">
                <InfoRow label="Location" value={[detail.city, detail.state].filter(Boolean).join(', ') || detail.zipcode || 'Not set'} />
                <InfoRow label="Sports" value={detail.sports?.join(', ') || 'Not set'} />
                <InfoRow label="Phone" value={detail.contactPhone || 'Not set'} />
                <InfoRow label="Email" value={detail.contactEmail || 'Not set'} />
                <InfoRow label="WhatsApp" value={detail.contactWhatsapp || 'Not set'} />
                <InfoRow label="Website" value={detail.websiteUrl || 'Not set'} />
                {detail.bio ? <p className="mt-3 text-sm leading-6 text-slate-300">{detail.bio}</p> : null}
              </Section>

              {detail.stringerDetails ? (
                <Section title="Stringer details">
                  <InfoRow label="Experience" value={detail.stringerDetails.experienceYears ? `${detail.stringerDetails.experienceYears} years` : 'Not set'} />
                  <InfoRow label="Brands" value={detail.stringerDetails.stringBrands?.join(', ') || 'Not set'} />
                  <InfoRow label="Pricing" value={detail.stringerDetails.basePriceMin ? `$${detail.stringerDetails.basePriceMin}${detail.stringerDetails.basePriceMax ? ` - $${detail.stringerDetails.basePriceMax}` : ''}` : 'Not set'} />
                  <InfoRow label="Turnaround" value={detail.stringerDetails.turnaroundHours ? `${detail.stringerDetails.turnaroundHours} hours` : 'Not set'} />
                </Section>
              ) : null}

              {detail.coachDetails ? (
                <Section title="Coach details">
                  <InfoRow label="Experience" value={detail.coachDetails.experienceYears ? `${detail.coachDetails.experienceYears} years` : 'Not set'} />
                  <InfoRow label="Lesson types" value={detail.coachDetails.lessonTypes?.join(', ') || 'Not set'} />
                  <InfoRow label="Specialties" value={detail.coachDetails.specialties?.join(', ') || 'Not set'} />
                  <InfoRow label="Rates" value={detail.coachDetails.hourlyRateMin ? `$${detail.coachDetails.hourlyRateMin}${detail.coachDetails.hourlyRateMax ? ` - $${detail.coachDetails.hourlyRateMax}` : ''}` : 'Not set'} />
                </Section>
              ) : null}

              {detail.academyDetails ? (
                <Section title="Academy details">
                  <InfoRow label="Academy" value={detail.academyDetails.academyName || 'Not set'} />
                  <InfoRow label="Courts" value={detail.academyDetails.numCourts ? `${detail.academyDetails.numCourts}` : 'Not set'} />
                  <InfoRow label="Programs" value={detail.academyDetails.programsOffered?.join(', ') || 'Not set'} />
                </Section>
              ) : null}

              <Section title="Reports">
                <div className="mb-3 grid grid-cols-2 gap-3">
                  <StatChip label="Total reports" value={String(detail.moderationSummary.totalReports)} />
                  <StatChip label="Pending" value={String(detail.moderationSummary.pendingReports)} />
                  <StatChip label="Reviewed" value={String(detail.moderationSummary.reviewedReports)} />
                  <StatChip label="Dismissed" value={String(detail.moderationSummary.dismissedReports)} />
                </div>
                {detail.moderationSummary.reasonBreakdown.length > 0 ? (
                  <div className="mb-4 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Reason breakdown</p>
                    <div className="mt-3 space-y-2">
                      {detail.moderationSummary.reasonBreakdown.map((reason) => (
                        <div key={reason.reasonCode} className="flex items-center justify-between gap-3 rounded-lg bg-slate-950/70 px-3 py-2 text-sm">
                          <div>
                            <p className="font-medium text-slate-200">{reason.reasonCode}</p>
                            <p className="text-xs text-slate-500">Last report {formatWhen(reason.lastReportedAt)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-200">{reason.totalCount} total</p>
                            <p className="text-xs text-amber-300">{reason.pendingCount} pending</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {detail.reports.length > 0 ? (
                  <div className="space-y-3">
                    {detail.reports.map((report) => (
                      <div key={report.id} className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-white">{report.reasonCode || 'Report'}</p>
                            <p className="mt-1 text-xs text-slate-400">Reported by user #{report.reportedByUserId}</p>
                          </div>
                          <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-300">
                            {report.status}
                          </span>
                        </div>
                        {report.reasonText ? (
                          <p className="mt-2 text-sm leading-6 text-slate-300">{report.reasonText}</p>
                        ) : null}
                        <p className="mt-2 text-xs text-slate-500">Created {formatWhen(report.createdAt)}</p>
                        {report.reviewNotes ? (
                          <p className="mt-2 rounded-lg bg-slate-950/80 px-3 py-2 text-xs text-slate-300">
                            {report.reviewNotes}
                          </p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No provider reports for this listing.</p>
                )}
              </Section>

              <Section title="Admin action">
                <textarea
                  value={reviewNotes}
                  onChange={(event) => setReviewNotes(event.target.value)}
                  rows={4}
                  placeholder="Optional review notes"
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-cyan-500 focus:outline-none"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  {getAllowedActions(detail).map((action) => (
                    <button
                      key={action}
                      onClick={() => void handleAction(action)}
                      disabled={actioning !== null || (action === 'PUBLISH' && publishBlocked)}
                      className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        action === 'REJECT' || action === 'SUSPEND' || action === 'ARCHIVE'
                          ? 'bg-rose-600 text-white hover:bg-rose-500'
                          : 'bg-cyan-600 text-white hover:bg-cyan-500'
                      } disabled:opacity-50`}
                    >
                      {actioning === action ? 'Working...' : actionLabel(action)}
                    </button>
                  ))}
                </div>
                {publishBlocked ? (
                  <p className="mt-2 text-xs text-rose-300">
                    Publish is disabled until the blocking issues above are resolved.
                  </p>
                ) : null}
              </Section>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-center text-slate-500">
              Select a provider listing to review.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-slate-800 pt-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-300">
        <CheckCircle2 className="h-4 w-4 text-cyan-400" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200">{value}</span>
    </div>
  );
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-200">{value}</p>
    </div>
  );
}

function IssueList({
  tone,
  title,
  items,
}: {
  tone: 'rose' | 'amber';
  title: string;
  items: string[];
}) {
  const classes =
    tone === 'rose'
      ? 'border-rose-800/50 bg-rose-900/20 text-rose-300'
      : 'border-amber-800/50 bg-amber-900/20 text-amber-300';
  const dot =
    tone === 'rose'
      ? 'bg-rose-300'
      : 'bg-amber-300';

  return (
    <div className={`rounded-lg border p-3 ${classes}`}>
      <p className="text-sm font-semibold">{title}</p>
      <ul className="mt-2 space-y-2 text-sm text-white/90">
        {items.map((issue) => (
          <li key={issue} className="flex gap-2">
            <span className={`mt-1 h-1.5 w-1.5 rounded-full ${dot}`} />
            <span>{issue}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
